import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { getGuide } from "@/lib/guides";

export const Route = createFileRoute("/guides/$slug")({
  loader: ({ params }) => {
    const guide = getGuide(params.slug);
    if (!guide) throw notFound();
    return guide;
  },
  head: ({ params, loaderData }) => {
    const g = loaderData;
    const url = `https://julsskin.shop/guides/${params.slug}`;
    if (!g) return { meta: [{ title: "Guide — Jul's Skin" }] };
    return {
      meta: [
        { title: g.metaTitle },
        { name: "description", content: g.description },
        { property: "og:title", content: g.metaTitle },
        { property: "og:description", content: g.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:title", content: g.metaTitle },
        { name: "twitter:description", content: g.description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: g.title,
            description: g.description,
            dateModified: g.updated,
            mainEntityOfPage: url,
            author: { "@type": "Organization", name: "Jul's Skin" },
            publisher: { "@type": "Organization", name: "Jul's Skin", url: "https://julsskin.shop" },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://julsskin.shop/" },
              { "@type": "ListItem", position: 2, name: "Guides", item: "https://julsskin.shop/guides" },
              { "@type": "ListItem", position: 3, name: g.title, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: GuidePage,
});

function GuidePage() {
  const g = Route.useLoaderData();

  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />
      <main id="main" className="mx-auto max-w-3xl w-full px-6 py-14 flex-1">
        <nav className="text-xs text-muted-foreground">
          <Link to="/" className="hover:text-foreground">Home</Link> ›{" "}
          <Link to="/guides" className="hover:text-foreground">Guides</Link>
        </nav>
        <h1 className="display text-3xl md:text-4xl mt-3">{g.title}</h1>
        <p className="mt-4 text-muted-foreground">{g.intro}</p>

        {g.sections.map((s) => (
          <section key={s.heading} className="mt-10">
            <h2 className="display text-2xl">{s.heading}</h2>
            {s.body.map((p, i) => (
              <p key={i} className="mt-3 text-sm leading-relaxed text-muted-foreground">{p}</p>
            ))}
          </section>
        ))}

        <section className="mt-12">
          <h2 className="display text-2xl">FAQ</h2>
          <dl className="mt-4 space-y-5">
            {g.faq.map((f) => (
              <div key={f.q}>
                <dt className="font-medium">{f.q}</dt>
                <dd className="mt-1 text-sm text-muted-foreground">{f.a}</dd>
              </div>
            ))}
          </dl>
        </section>

        <div className="mt-12 rounded-2xl border border-border bg-card/60 p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            Shop natural skincare and hair care made in small batches.
          </p>
          <Link
            to="/shop"
            className="inline-flex items-center justify-center bg-foreground text-background px-6 py-3 text-sm font-medium rounded hover:opacity-90"
          >
            Shop the collection
          </Link>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
